import { useState } from 'react'

import { TypedDocumentNode } from '@apollo/client'
import { Box, EnhancedDimension, Icon, SearchField } from '@factorialco/deprecated-design-system'
import { Exact, PaginatedQueryPageInfoFragment } from 'generated/resources/graphql'

import LabeledInput from 'modules/core/components/LabeledInput'
import PopoverMenu from 'modules/core/components/PopoverMenu'
import { useCallback } from 'modules/core/hooks/useCallback'
import { UseQueryType } from 'modules/core/hooks/useQuery'
import { useDebouncedSearch } from 'modules/core/hooks/useSearch'
import i18n from 'modules/core/lib/i18n'

import { ClearValueButton } from '../Filter/base/ClearValue/ClearValueButton'
import { PaginatedQueryContainer } from './PaginatedQueryContainer'

interface SingleEntitySelectorProps<
  TResult,
  Node extends { [key: string]: unknown },
  Variables extends { [key: string]: unknown },
  Edge extends { cursor: string; node: Node }
> {
  document: TypedDocumentNode<TResult, Variables>
  variables: Omit<Exact<Variables>, 'search' | 'after'>
  extractConnection: (data: NonNullable<UseQueryType<TResult, Variables>['data']>) => {
    pageInfo: PaginatedQueryPageInfoFragment
    edges: Array<Edge>
  }
  renderEntity: (entity: Node) => JSX.Element
  value: Node | null
  onChange: (value: Node | null) => void
  onPopoverClose?: () => void
  renderValue: (value: Node | null) => JSX.Element
  disabled?: boolean
  error?: string
  mandatory?: boolean
  label?: string
  description?: string
  filterEntities?: (entities: Node[]) => Node[]
  renderSkeleton?: () => JSX.Element
  width?: EnhancedDimension
  minWidth?: EnhancedDimension
  hideSearchIcon?: boolean
  hideLabel?: boolean
}

export const SingleEntitySelector = <
  TResult,
  Node extends { [key: string]: unknown },
  Variables extends { [key: string]: unknown },
  Edge extends { cursor: string; node: Node }
>({
  value,
  renderValue,
  onChange,
  document,
  renderEntity,
  variables,
  extractConnection,
  error,
  mandatory,
  label,
  description,
  width,
  minWidth,
  renderSkeleton,
  filterEntities,
  onPopoverClose,
  disabled = false,
  hideSearchIcon = false,
  hideLabel = false,
}: SingleEntitySelectorProps<TResult, Node, Variables, Edge>) => {
  const [popoverOpen, setPopoverOpen] = useState(false)
  const { search, onSearch, debouncedSearch } = useDebouncedSearch()
  const [hoveringIndex, setHoveringIndex] = useState<string | null>(null)

  const unhover = useCallback(() => setHoveringIndex(null), [])
  const hover = useCallback((index: string) => setHoveringIndex(index), [])

  const handleToggle = () => {
    setPopoverOpen(!popoverOpen)
    if (popoverOpen) {
      onPopoverClose?.()
    }
  }

  return (
    <PopoverMenu type="dialog" onClick={handleToggle} open={popoverOpen}>
      <LabeledInput
        aria-label="entity-selector"
        description={description}
        readonly={disabled}
        error={error}
        mandatory={mandatory}
        hideLabel={hideLabel}
        label={label}
        value={value?.toString() ?? undefined}
      >
        <PopoverMenu.Trigger disabled={disabled}>
          <Box
            paddingX="s16"
            paddingY="s8"
            flexDirection="row"
            height="s40"
            alignItems="center"
            width="full"
            cursor="pointer"
          >
            <Box flexDirection="row" width="full" gap="s6">
              {!hideSearchIcon && (
                <Icon.Medium icon="search" color={disabled ? 'grey600' : 'grey800'} />
              )}
              <Box overflowX="hidden" textAlign="left">
                {renderValue(value)}
              </Box>
            </Box>
            <Icon.Small icon="caret-down" />
          </Box>
        </PopoverMenu.Trigger>
      </LabeledInput>
      <PopoverMenu.Content width={'full'} side="bottom" align="center" offset={0}>
        <div aria-label="entity-selector-popover">
          <Box width={width} minWidth={minWidth}>
            <Box padding="s12">
              <SearchField variant="big" onSearch={onSearch} search={search} placeholder="" />
            </Box>
          </Box>
          <PaginatedQueryContainer<TResult, Node, Variables, Edge>
            document={document}
            height="s360"
            width={width}
            minWidth={minWidth}
            variables={{ ...variables, search: debouncedSearch }}
            extractConnection={extractConnection}
            filterEntities={filterEntities}
            loadingContent={
              renderSkeleton
                ? () => (
                    <>
                      <BoxRow
                        id={'skeleton-1'}
                        hover={hover}
                        unhover={unhover}
                        hoveringIndex={hoveringIndex}
                      >
                        {renderSkeleton()}
                      </BoxRow>
                      <BoxRow
                        id={'skeleton-2'}
                        hover={hover}
                        unhover={unhover}
                        hoveringIndex={hoveringIndex}
                      >
                        {renderSkeleton()}
                      </BoxRow>
                      <BoxRow
                        id={'skeleton-3'}
                        hover={hover}
                        unhover={unhover}
                        hoveringIndex={hoveringIndex}
                      >
                        {renderSkeleton()}
                      </BoxRow>
                    </>
                  )
                : undefined
            }
            content={(node) => {
              const key = `${node.id}`
              const isSelected = `${value?.id}` === key
              const onNodeClick = () => {
                onChange(node)
                setPopoverOpen(false)
              }

              return (
                <BoxRow
                  id={key}
                  isSelected={isSelected}
                  hover={hover}
                  unhover={unhover}
                  onClick={onNodeClick}
                  hoveringIndex={hoveringIndex}
                >
                  {renderEntity(node)}
                </BoxRow>
              )
            }}
          />

          {value !== null && (
            <Box width="full" style={{ marginBottom: '-12px' }}>
              <ClearValueButton onClear={() => onChange(null)}>
                {i18n.t('common.clear_selection')}
              </ClearValueButton>
            </Box>
          )}
        </div>
      </PopoverMenu.Content>
    </PopoverMenu>
  )
}

const BoxRow = ({
  id,
  hover,
  unhover,
  hoveringIndex,
  isSelected,
  onClick,
  children,
}: React.PropsWithChildren<{
  id: string
  hover: (key: string) => void
  unhover: () => void
  hoveringIndex: string | null
  isSelected?: boolean
  onClick?: () => void
}>) => {
  return (
    <Box
      key={id}
      component="label"
      paddingX="s16"
      paddingY="s12"
      justifyContent="spaceBetween"
      cursor="pointer"
      onMouseEnter={() => hover(id)}
      onMouseLeave={unhover}
      background={id == hoveringIndex ? 'grey200' : isSelected ? 'viridian100' : undefined}
      onClick={onClick}
    >
      {children}
    </Box>
  )
}
